import { ChartOptions, TimeUnit } from 'chart.js';
import { format, formatDate, isDate, parse } from 'date-fns';
import { tz, TZDate } from '@date-fns/tz';

interface Config {
    dateFormat?: string;
    intervalUnit?: TimeUnit;
    yMeasurementUnit?: string;
    timeZone?: string;
}

export function getDefaultLineChartOptions({
    dateFormat,
    intervalUnit,
    yMeasurementUnit,
    timeZone,
}: Config = {}): Partial<ChartOptions<'line'>> {
    return {
        interaction: {
            mode: 'nearest',
            axis: 'xy',
            intersect: false,
        },
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: false,
            },
            title: {
                display: false,
            },
            tooltip: {
                enabled: true,
                usePointStyle: true,
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                bodyColor: 'rgba(17, 30, 44, 1)',
                bodyFont: {
                    family: 'Sora',
                    weight: 700,
                    size: 14,
                    lineHeight: '14px',
                },
                borderColor: 'rgba(113, 113, 113, 0.1)',
                borderWidth: 2,
                footerColor: 'rgba(113, 113, 113, 1)',
                footerFont: {
                    family: 'Sora',
                    size: 10,
                    weight: 400,
                    lineHeight: '10px',
                },
                footerAlign: 'center',
                callbacks: {
                    title() {
                        return '';
                    },
                    label(tooltipItem) {
                        return `${tooltipItem.formattedValue}${yMeasurementUnit}`;
                    },
                    footer(tooltipItems) {
                        const [item] = tooltipItems;
                        if (item?.label == null) return '';
                        const parsedDate = parse(
                            item.label,
                            'yyyy-MM-dd hh:mm:ss a X',
                            new TZDate(new Date(), timeZone),
                            { in: timeZone == null ? undefined : tz(timeZone) },
                        );
                        if (intervalUnit === 'hour') {
                            return format(parsedDate, 'hh:mma');
                        }
                        if (intervalUnit === 'day') {
                            return format(parsedDate, 'MMM d');
                        }
                        if (intervalUnit === 'month') {
                            return format(parsedDate, 'MMM yyyy');
                        }
                        // unit/interval needs to be cleaned up
                        throw Error('unexpected configuration');
                    },
                },
            },
        },
        scales: {
            y: {
                ticks: {
                    color: '#b8b8b8',
                    font: {
                        family: 'Sora',
                        size: 10,
                        weight: 'normal',
                        lineHeight: 1,
                    },
                    callback: (value) => {
                        const formattedValue = Math.round(Number(value)).toString();
                        return yMeasurementUnit != null
                            ? `${formattedValue} ${yMeasurementUnit}`
                            : formattedValue;
                    },
                    maxTicksLimit: 4,
                },
                grid: { drawTicks: false },
                border: {
                    display: false,
                    dash: [1, 1],
                },
            },
            x: {
                type: 'time',
                adapters: { date: { timeZone } },
                time: {
                    tooltipFormat: 'yyyy-MM-dd hh:mm:ss a X',
                    unit: intervalUnit,
                    ...(dateFormat != null && {
                        displayFormats: {
                            [intervalUnit as string]: dateFormat,
                        },
                    }),
                    round: intervalUnit,
                },
                ticks: {
                    callback(value) {
                        const date = new Date(value);
                        if (dateFormat == null || !isDate(date)) return '';
                        return formatDate(date, dateFormat, {
                            in: timeZone == null ? undefined : tz(timeZone),
                        });
                    },
                    color: '#b8b8b8',
                    font: {
                        family: 'Sora',
                        size: 10,
                        weight: 'normal',
                        lineHeight: 1,
                    },
                    padding: 10,
                    align: 'center',
                    maxTicksLimit: 4,
                },
                grid: { drawTicks: false, display: false },
                border: { display: false },
            },
        },
    };
}
